import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2, Gift } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api } from "@/lib/apiClient";
import { trackInitiateCheckout } from "@/lib/tracking";

const PRODUCTS = {
  discovery_pack: {
    name: "Diagnostic Inaugural",
    price: "Gratuit",
    value: 0,
    free: true,
    tagline: "Votre première lecture, sans engagement.",
    points: ["Lecture de votre matrice numérologique", "Identification d'un verrou de lignée", "Appel de restitution personnalisé"],
  },
  holistic_wellness: {
    name: "Analyse de Précision",
    price: "149 €",
    value: 149,
    free: false,
    tagline: "Cartographie vibratoire complète.",
    points: ["Grille Lo Shu personnelle", "Rapport vibratoire détaillé", "Recommandations Feng Shui"],
  },
  quantum_wellness: {
    name: "Pack Renaissance Quantique",
    price: "250 €",
    value: 250,
    free: false,
    tagline: "Transformation guidée sur 90 jours.",
    points: ["Analyse de Précision incluse", "Levée des dettes karmiques", "Protocole de renaissance"],
  },
  elite_coaching: {
    name: "Coaching Élite — Métamorphose",
    price: "1 950 €",
    value: 1950,
    free: false,
    tagline: "L'accompagnement souverain absolu.",
    points: [
      "12 séances personnalisées",
      "Lecture Triple Fusion™ complète",
      "Accès direct à Rodrigue CASSILDÉ",
      "Accès au Cercle privé",
    ],
  },
  programme_souverain: {
    name: "Programme Souverain",
    price: "3 200 €",
    value: 3200,
    free: false,
    tagline: "L'engagement le plus profond de l'Académie.",
    points: [
      "24 séances personnalisées",
      "Suivi individuel sur 24 semaines",
      "Lecture Triple Fusion™ approfondie",
      "Accompagnement continu de Rodrigue CASSILDÉ",
      "Accès prioritaire au Cercle privé",
    ],
  },
  "kit-digital-premium": {
    name: "Kit Digital Premium",
    price: "67 €",
    value: 67,
    free: false,
    tagline: "Vos guides Triple Fusion™ — accès immédiat.",
    points: [
      "Accès instantané à tous les guides numériques",
      "Fiches pratiques téléchargeables (PDF)",
      "Exercices de transformation personnelle",
      "Méditations guidées exclusives",
    ],
  },
};

export default function Checkout() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const product = PRODUCTS[productId];

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", birthdate: "", message: "" });

  useEffect(() => {
    if (product) {
      trackInitiateCheckout({ value: product.value, packageId: productId });
    }
    if (searchParams.get("cancelled")) {
      toast.error("Paiement annulé. Vous pouvez réessayer quand vous le souhaitez.");
    }
  }, [productId]); // eslint-disable-line

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="checkout-notfound">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-white mb-4">Offre introuvable</h1>
          <button onClick={() => navigate("/services")} className="text-gold uppercase tracking-[0.15em] text-sm">
            Voir tous les parcours
          </button>
        </div>
      </div>
    );
  }

  const handleFreeSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Merci de renseigner votre nom, email et téléphone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, product: productId });
      navigate(`/merci?product=${productId}`);
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
      setLoading(false);
    }
  };

  const handlePaidCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: productId,
        origin_url: window.location.origin,
        customer_email: form.email || null,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("no url");
      }
    } catch (err) {
      toast.error("Impossible d'initier le paiement. Merci de réessayer.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-24" data-testid="checkout-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        {/* Summary */}
        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="lg:sticky lg:top-28"
        >
          <p className="text-xs uppercase tracking-luxe text-gold mb-6">Finaliser votre parcours</p>
          <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight">{product.name}</h1>
          <p className="text-white/50 mt-4 font-light text-lg">{product.tagline}</p>

          <div className="mt-8 flex items-baseline gap-3">
            <span className="font-serif text-5xl text-gold">{product.price}</span>
            {product.free && <Gift className="text-gold" strokeWidth={1.5} />}
          </div>

          <ul className="mt-8 space-y-4 border-t border-white/10 pt-8">
            {product.points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-white/70">
                <Check size={18} className="text-gold shrink-0 mt-0.5" strokeWidth={2} />
                {p}
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Form / Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}
          className="border border-white/10 bg-brand-surface p-8 md:p-10"
        >
          {product.free ? (
            <form onSubmit={handleFreeSubmit} data-testid="lead-form" className="space-y-6">
              <h2 className="font-serif text-2xl text-white mb-2">Réservez votre diagnostic</h2>
              <Field label="Nom complet *" testid="lead-name"
                value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
              <Field label="Email *" type="email" testid="lead-email"
                value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Field label="Téléphone *" testid="lead-phone"
                value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <Field label="Date de naissance" type="date" testid="lead-birthdate"
                value={form.birthdate} onChange={(v) => setForm({ ...form, birthdate: v })} />
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-white/40 mb-2">Votre intention (optionnel)</label>
                <textarea
                  data-testid="lead-message"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold rounded-none text-white px-0 py-3 outline-none transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                data-testid="lead-submit"
                disabled={loading}
                className="w-full bg-gold text-brand-ink hover:bg-brand-gold-hover transition-colors px-8 py-5 text-sm uppercase tracking-[0.15em] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <>Réserver mon diagnostic <ArrowUpRight size={18} /></>}
              </button>
              <p className="text-xs text-white/30 text-center">Vos données restent confidentielles (voir Confidentialité).</p>
            </form>
          ) : (
            <div className="space-y-6" data-testid="paid-checkout">
              <h2 className="font-serif text-2xl text-white mb-2">Paiement sécurisé</h2>
              <p className="text-white/50 font-light">
                Renseignez votre email pour recevoir votre confirmation, puis procédez au paiement sécurisé via Stripe.
              </p>
              <Field label="Email de facturation" type="email" testid="checkout-email"
                value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <button
                onClick={handlePaidCheckout}
                data-testid="checkout-pay-button"
                disabled={loading}
                className="w-full bg-gold text-brand-ink hover:bg-brand-gold-hover transition-colors px-8 py-5 text-sm uppercase tracking-[0.15em] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <>Commander — {product.price} <ArrowUpRight size={18} /></>}
              </button>
              <div className="flex items-center justify-center gap-2 text-xs text-white/30">
                <Check size={14} className="text-gold" /> Paiement chiffré · Stripe
              </div>

              {productId === "elite_coaching" && (
                <div
                  data-testid="elite-3x-block"
                  className="mt-8 relative border border-gold/40 rounded-sm p-6 md:p-7"
                  style={{ backgroundColor: "rgba(201,169,110,0.05)" }}
                >
                  <span
                    className="absolute -top-3 left-6 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold"
                    style={{ backgroundColor: "#C9A96E", color: "#0A0A12" }}
                  >
                    Facilité de paiement · 3× sans frais
                  </span>

                  <h3 className="font-serif text-xl md:text-2xl text-white mt-3 mb-1">
                    Paiement en 3 fois sans frais — <span className="text-gold">650 €/mois</span>
                  </h3>
                  <p className="text-white/50 text-sm font-light mb-6">
                    Une alternative accessible au règlement intégral, sans surcoût.
                  </p>

                  <a
                    href="https://buy.stripe.com/7sY14ndVKbvw8Z62VN0sU06"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="elite-3x-cta"
                    className="w-full border-2 border-gold text-gold hover:bg-gold hover:text-brand-ink transition-colors px-6 py-4 text-sm uppercase tracking-[0.15em] font-semibold inline-flex items-center justify-center gap-2"
                  >
                    Versement 1/3 — 650 € <ArrowUpRight size={16} />
                  </a>

                  <p className="mt-4 text-xs text-white/45 leading-relaxed">
                    Puis Versement 2/3 (J+30) et Versement 3/3 (J+60) —
                    liens envoyés après confirmation du premier versement.
                  </p>

                  <details className="mt-4 group">
                    <summary className="cursor-pointer text-[11px] uppercase tracking-[0.15em] text-white/40 hover:text-white/70 transition-colors">
                      Voir les liens des versements 2 et 3 (transparence)
                    </summary>
                    <ul className="mt-3 space-y-2 text-xs text-white/45 font-light pl-4">
                      <li>
                        Versement 2/3 — 650 € (J+30) :{" "}
                        <a
                          href="https://buy.stripe.com/dRmeVd6ti5780sA2VN0sU07"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gold/70 hover:text-gold underline break-all"
                        >
                          buy.stripe.com/…0sU07
                        </a>
                      </li>
                      <li>
                        Versement 3/3 — 650 € (J+60) :{" "}
                        <a
                          href="https://buy.stripe.com/fZu3cv2d2czAejq67Z0sU08"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gold/70 hover:text-gold underline break-all"
                        >
                          buy.stripe.com/…0sU08
                        </a>
                      </li>
                    </ul>
                  </details>
                </div>
              )}

              {productId === "programme_souverain" && (
                <div
                  data-testid="souverain-4x-block"
                  className="mt-8 relative border border-gold/40 rounded-sm p-6 md:p-7"
                  style={{ backgroundColor: "rgba(201,169,110,0.05)" }}
                >
                  <span
                    className="absolute -top-3 left-6 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold"
                    style={{ backgroundColor: "#C9A96E", color: "#0A0A12" }}
                  >
                    Facilité de paiement · 4 mensualités
                  </span>

                  <h3 className="font-serif text-xl md:text-2xl text-white mt-3 mb-1">
                    Paiement en 4 fois — <span className="text-gold">875 €/mois</span>
                  </h3>
                  <p className="text-white/50 text-sm font-light mb-6">
                    Total 3 500 € · annulation automatique après le 4ᵉ prélèvement. Comptant à 3 200 € = <span className="text-gold">économie de 300 €</span>.
                  </p>

                  <a
                    href="https://buy.stripe.com/fZu14n6ti4345MUeEv0sU0d"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="souverain-4x-cta"
                    className="w-full border-2 border-gold text-gold hover:bg-gold hover:text-brand-ink transition-colors px-6 py-4 text-sm uppercase tracking-[0.15em] font-semibold inline-flex items-center justify-center gap-2"
                  >
                    Souscrire au fractionné — 875 €/mois <ArrowUpRight size={16} />
                  </a>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.15em] text-white/40 mb-2">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border-b border-white/20 focus:border-gold rounded-none text-white px-0 py-3 outline-none transition-colors [color-scheme:dark]"
    />
  </div>
);
